import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Sidebar from "./Sidebar";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Attribution = () => {
  const [dateRange, setDateRange] = useState("Last 30 Days");
  const [hasAdData, setHasAdData] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("");

  useEffect(() => {
    // Simulate API call to check if ad data exists
    const fetchAdDataStatus = async () => {
      // Replace this with actual API call
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8080";
      console.log("Using API URL csv:", apiUrl);
      const token = localStorage.getItem("token"); // Assuming the token is stored in localStorage
      const response = await fetch(`${apiUrl}/api/csv/check-ad-data`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`, // Include the Bearer token
            "Content-Type": "application/json",
          },
        }); 
      const data = await response.json();
      setHasAdData(data.hasAdData);
    };
    fetchAdDataStatus();
  }, []);

  const handleDateChange = (event) => setDateRange(event.target.value);

  const handleExportData = () => {
    console.log("Export data functionality triggered.");
  };

  const handlePlatformChange = (event) => setSelectedPlatform(event.target.value);

  const handleFileUpload = async (file) => {
    if (!file || !selectedPlatform) {
      setUploadStatus("Please select an ad platform and upload a file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("platform", selectedPlatform);
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8080";
    console.log("Using API URL csv:", apiUrl);

    const token = localStorage.getItem("token"); // Ensure token is stored during signup/login
    if (!token) {
      console.error("Authentication token is missing. Please log in again.");
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/api/csv/upload-ad-csv/${selectedPlatform.toLowerCase()}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`, // Include the Bearer token
        },
        body: formData,
      });

      if (response.ok) {
        setUploadStatus("Ad data uploaded successfully!");
        setHasAdData(true); // Simulate refresh to show charts
      } else {
        setUploadStatus("Failed to upload ad data. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading ad data:", error);
      setUploadStatus("Error uploading ad data. Please try again.");
    }
  };

  const attributionData = {
    labels: ["Google Ads", "Meta Ads", "Email Campaigns", "Direct Traffic", "Organic Search"],
    datasets: [
      {
        label: "Revenue Contribution ($)",
        data: [5000, 4000, 3500, 2000, 1500],
        backgroundColor: ["#f44336", "#2196f3", "#4caf50", "#ff9800", "#9c27b0"],
        hoverBackgroundColor: ["#e57373", "#64b5f6", "#81c784", "#ffb74d", "#ba68c8"],
      },
    ],
  };

  const attributionTableData = [
    { source: "Google Ads", revenue: "$5,000", roi: "4.5x", contribution: "30%" },
    { source: "Meta Ads", revenue: "$4,000", roi: "3.8x", contribution: "25%" },
    { source: "Email Campaigns", revenue: "$3,500", roi: "6.2x", contribution: "20%" },
    { source: "Direct Traffic", revenue: "$2,000", roi: "8.0x", contribution: "15%" },
    { source: "Organic Search", revenue: "$1,500", roi: "10.5x", contribution: "10%" },
  ];

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f9f9f9" }}>
      <Box sx={{ flexGrow: 1, padding: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Attribution
            </Typography>
            <Typography variant="subtitle1">
              Analyze the revenue contribution and ROI of each marketing channel.
            </Typography>
          </Box>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Date Range</InputLabel>
            <Select value={dateRange} onChange={handleDateChange} label="Date Range">
              <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
              <MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
              <MenuItem value="Last 90 Days">Last 90 Days</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {!hasAdData && (
          <Paper sx={{ padding: 3, borderRadius: 2, mb: 5, backgroundColor: "#fff3e0" }}>
            <Typography variant="h6" gutterBottom>
              Get Started with Attribution
            </Typography>
            <Typography variant="body1" gutterBottom>
              Select an ad platform and upload your ad data to start analyzing your marketing performance.
            </Typography>
            <Box sx={{ mt: 2, mb: 2 }}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 2 }}>
                <InputLabel>Ad Platform</InputLabel>
                <Select
                  value={selectedPlatform}
                  onChange={handlePlatformChange}
                  label="Ad Platform"
                >
                  <MenuItem value="google">Google Ads</MenuItem>
                  <MenuItem value="meta">Meta Ads</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
              <input
                type="file"
                accept=".csv"
                style={{ display: "none" }}
                id="upload-file"
                onChange={(e) => handleFileUpload(e.target.files[0])}
              />
              <label htmlFor="upload-file">
                <Button variant="contained" color="primary" component="span">
                  Upload Ad Data
                </Button>
              </label>
            </Box>
            {uploadStatus && (
              <Typography
                variant="body2"
                sx={{ color: uploadStatus.includes("successfully") ? "green" : "red", mt: 1 }}
              >
                {uploadStatus}
              </Typography>
            )}
          </Paper>
        )}

        {hasAdData && (
          <>
            <Paper sx={{ padding: 3, borderRadius: 2, mb: 5 }}>
              <Typography variant="h6" gutterBottom>
                Revenue Contribution by Channel
              </Typography>
              <Box sx={{ height: 400, width: "100%" }}>
                <Bar
                  data={attributionData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "top",
                      },
                      tooltip: {
                        callbacks: {
                          label: (tooltipItem) => `$${tooltipItem.raw.toLocaleString()}`,
                        },
                      },
                    },
                    scales: {
                      x: { grid: { display: false } },
                      y: { grid: { color: "#e0e0e0" } },
                    },
                  }}
                />
              </Box>
            </Paper>

            <Paper sx={{ padding: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Channel Attribution Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Channel</TableCell>
                      <TableCell>Revenue</TableCell>
                      <TableCell>ROI</TableCell>
                      <TableCell>Contribution</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attributionTableData.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: "#f5f5f5" },
                          "&:hover": { backgroundColor: "#e0f7fa" },
                        }}
                      >
                        <TableCell>{row.source}</TableCell>
                        <TableCell>{row.revenue}</TableCell>
                        <TableCell>{row.roi}</TableCell>
                        <TableCell>{row.contribution}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Button variant="outlined" sx={{ mt: 2 }} onClick={handleExportData}>
                Export Data
              </Button>
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Attribution;
